import { Badge } from '@thegpvc/ui';
import { SectionCard } from './SectionCard';
import { cx, text } from '../styles/theme';
import type { CoachingInsight } from '../types/api';

const SEVERITY_VARIANT: Record<string, 'error' | 'warning' | 'neutral'> = {
  high: 'error',
  medium: 'warning',
  low: 'neutral',
};
const SEVERITY_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

type Group = {
  type: string;
  title: string;
  recommendation: string;
  severity: string;
  chapters: number[]; // grounded chapter ordinals, ascending
  sessionLevel: boolean; // at least one instance was session-level (no chapter)
};

// groupByType collapses the per-chapter insights into one row per type, so a
// signal that recurred across several chapters reads as "chapters 3, 7, 8"
// rather than three near-identical cards. The full detail + evidence lives in
// each chapter (linked through), keeping the summary a compact index.
function groupByType(insights: CoachingInsight[]): Group[] {
  const byType = new Map<string, Group>();
  for (const it of insights) {
    let g = byType.get(it.type);
    if (!g) {
      g = {
        type: it.type,
        title: it.title,
        recommendation: it.recommendation,
        severity: it.severity,
        chapters: [],
        sessionLevel: false,
      };
      byType.set(it.type, g);
    }
    if ((SEVERITY_RANK[it.severity] ?? 0) > (SEVERITY_RANK[g.severity] ?? 0)) {
      g.severity = it.severity;
    }
    if (typeof it.sittingOrdinal === 'number') {
      if (!g.chapters.includes(it.sittingOrdinal)) g.chapters.push(it.sittingOrdinal);
    } else {
      g.sessionLevel = true;
    }
  }
  const groups = [...byType.values()];
  for (const g of groups) g.chapters.sort((a, b) => a - b);
  // Most severe first.
  groups.sort((a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0));
  return groups;
}

/**
 * CoachingRollup is the compact, session-summary view of the coaching insights:
 * one row per signal type, with the chapters it occurred in as link-through
 * chips (the full detail + evidence is shown in the chapter itself). Session-
 * level signals (e.g. cache reuse) show a "session-wide" tag instead of a chip.
 * Renders nothing when there are no insights.
 */
export function CoachingRollup({
  insights,
  onChapter,
}: {
  insights?: CoachingInsight[];
  onChapter?: (ordinal: number) => void;
}) {
  if (!insights || insights.length === 0) return null;
  const groups = groupByType(insights);
  return (
    <SectionCard title="Coaching">
      <ul className="space-y-3">
        {groups.map((g) => (
          <li key={g.type} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={SEVERITY_VARIANT[g.severity] ?? 'neutral'}>{g.severity}</Badge>
              <span className={cx('font-medium', text.primary)}>{g.title}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
              {g.sessionLevel && <span className={text.muted}>session-wide</span>}
              {g.chapters.length > 0 && <span className={text.muted}>in</span>}
              {g.chapters.map((ord) =>
                onChapter ? (
                  <button
                    key={ord}
                    type="button"
                    onClick={() => onChapter(ord)}
                    className={cx(
                      'rounded-md px-2 py-0.5 text-xs font-medium',
                      'bg-orange-500/10 text-orange-600 hover:bg-orange-500/20',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
                    )}
                  >
                    Chapter {ord + 1}
                  </button>
                ) : (
                  <span key={ord} className={cx('text-xs font-medium', text.secondary)}>
                    Chapter {ord + 1}
                  </span>
                ),
              )}
            </div>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
